import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import type {ReactNode} from 'react'

import styles from './IssuesListSlots.module.css'

export type IssuesListSlotsProps = {
  header: ReactNode
  filterBar: ReactNode
  results: ReactNode
  /**
   * Accessible name for the filter `<search>` landmark.
   * @default 'Filter issues'
   */
  filterLabel?: string
  /**
   * Accessible name for the results `<section>` landmark.
   * @default 'Issues'
   */
  resultsLabel?: string
  /**
   * Wrap the slots in a flex-column container. Dashboard pages need this so the
   * header and body stack with consistent spacing. Pages that supply their own
   * outer layout (e.g. the repo-scoped IssueIndexPage) pass `false`.
   * @default true
   */
  wrap?: boolean
}

/**
 * Issues-specific arrangement for the content slot of `FilteredListPageLayout`.
 * Places the filter row in a `<search>` landmark and the results in a
 * `<section>` landmark, with the list header rendered above both (outside any
 * landmark, matching legacy stacking). Centralizes the markup so every issues
 * dashboard / index page renders identical, accessible slot structure.
 *
 * This is deliberately local to issues-react — it encodes issues' header/filter/
 * results ordering and spacing, not a reusable template. If a second feature
 * area needs the same arrangement, promote it into `@github-ui/layout-primitives`.
 */
export function IssuesListSlots({
  header,
  filterBar,
  results,
  filterLabel = 'Filter issues',
  resultsLabel = 'Issues',
  wrap = true,
}: IssuesListSlotsProps) {
  const body = (
    <>
      {header}
      <div className={styles.slottedListBody}>
        <FilteredListPageLayout.FilterBar aria-label={filterLabel}>{filterBar}</FilteredListPageLayout.FilterBar>
        <FilteredListPageLayout.Results aria-label={resultsLabel}>{results}</FilteredListPageLayout.Results>
      </div>
    </>
  )

  return wrap ? <div className={styles.slottedList}>{body}</div> : body
}
